/**
 * Crime & Safety (Violent Crime Rate) data service.
 * Source: CDPH Healthy Communities Indicator — Violent Crime Rate, California 2000–2013
 * Table: public.crime_safety_place
 *
 * Columns used:
 *   geofips, year, location_name, violent_crime_rate_per_100k, crime_safety_risk_percentile,
 *   violent_crime_count, population
 *
 * California comparison uses state-level geofips variants (6, '6', '0000006').
 */

import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CrimeSafetyRow {
  year: number;
  violent_crime_rate_per_100k: number | null;
  crime_safety_risk_percentile: number | null;
  violent_crime_count: number | null;
  population: number | null;
  location_name: string | null;
}

export interface CrimeSafetyBundle {
  hasData: boolean;
  locationDisplayName: string;
  /** geofips string used for queries */
  locationGeofips: string;
  /** All years with data, ascending */
  years: number[];
  /** Rate per 100k for each year (aligned to `years`) */
  localRatePer100k: (number | null)[];
  /** CA rate per 100k for each year (aligned to `years`) */
  caRatePer100k: (number | null)[];
  /** Latest year in local data */
  latestYear: number | null;
  latestLocalRate: number | null;
  latestLocalPercentile: number | null;
  latestCARate: number | null;
  fetchWarnings: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normGeofips(g: unknown): string {
  const s = String(g ?? '').trim();
  return s.replace(/^0+/, '') || '0';
}

function geofipsVariants(g: string): (string | number)[] {
  const raw = String(g).trim();
  const stripped = raw.replace(/^0+/, '') || '0';
  const set = new Set<string | number>([raw, stripped]);
  if (/^\d+$/.test(stripped)) {
    const n = Number(stripped);
    if (Number.isFinite(n)) set.add(n);
    set.add(stripped.padStart(7, '0'));
  }
  return Array.from(set);
}

const CA_VARIANTS: (string | number)[] = [6, '6', '0000006'];

function parseNum(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

// ─── Loader ───────────────────────────────────────────────────────────────────

export async function loadCrimeSafetyBundle(location: string): Promise<CrimeSafetyBundle> {
  const warnings: string[] = [];

  const empty = (locationDisplayName = location, locationGeofips = ''): CrimeSafetyBundle => ({
    hasData: false,
    locationDisplayName,
    locationGeofips,
    years: [],
    localRatePer100k: [],
    caRatePer100k: [],
    latestYear: null,
    latestLocalRate: null,
    latestLocalPercentile: null,
    latestCARate: null,
    fetchWarnings: warnings,
  });

  // 1. Resolve geofips
  await supabaseLocationService.initialize();
  const geofipsNum = await supabaseLocationService.getGeofips(location);
  if (!geofipsNum) {
    warnings.push(`Could not resolve geofips for "${location}".`);
    return empty();
  }

  const locGeofips = normGeofips(geofipsNum);
  const locVariants = geofipsVariants(locGeofips);

  // 2. Fetch all local rows, ordered by year
  const { data: localRows, error: localErr } = await supabaseClient
    .from('crime_safety_place')
    .select('year, violent_crime_rate_per_100k, crime_safety_risk_percentile, violent_crime_count, population, location_name')
    .in('geofips', locVariants)
    .order('year', { ascending: true });

  if (localErr) {
    warnings.push(`Error fetching local crime data: ${localErr.message}`);
    return empty(location, locGeofips);
  }

  if (!localRows || localRows.length === 0) {
    warnings.push(`No crime & safety rows found for "${location}".`);
    return empty(location, locGeofips);
  }

  const rows = localRows as CrimeSafetyRow[];
  const displayName = rows[0].location_name || location;

  // 3. Fetch California rows for the same years
  const years = rows.map((r) => r.year);
  const { data: caRows } = await supabaseClient
    .from('crime_safety_place')
    .select('year, violent_crime_rate_per_100k')
    .in('geofips', CA_VARIANTS)
    .in('year', years)
    .order('year', { ascending: true });

  const caByYear = new Map<number, number | null>();
  if (caRows) {
    for (const r of caRows as { year: number; violent_crime_rate_per_100k: number | null }[]) {
      caByYear.set(r.year, parseNum(r.violent_crime_rate_per_100k));
    }
  }

  // 4. Build aligned arrays
  const localRates: (number | null)[] = rows.map((r) => parseNum(r.violent_crime_rate_per_100k));
  const caRates: (number | null)[] = years.map((y) => caByYear.get(y) ?? null);

  // 5. Latest-year metrics
  const latestRow = rows[rows.length - 1];
  const latestYear = latestRow.year;
  const latestLocalRate = parseNum(latestRow.violent_crime_rate_per_100k);
  const latestLocalPercentile = parseNum(latestRow.crime_safety_risk_percentile);
  const latestCARate = caByYear.get(latestYear) ?? null;

  return {
    hasData: true,
    locationDisplayName: displayName,
    locationGeofips: locGeofips,
    years,
    localRatePer100k: localRates,
    caRatePer100k: caRates,
    latestYear,
    latestLocalRate,
    latestLocalPercentile,
    latestCARate,
    fetchWarnings: warnings,
  };
}

// ─── Narrative builder ────────────────────────────────────────────────────────

export function buildCrimeSafetyOverviewNarrative(location: string, bundle: CrimeSafetyBundle): string {
  if (!bundle.hasData) return `No violent-crime rate data found for ${location} in Supabase.`;

  const { latestYear, latestLocalRate, latestLocalPercentile, latestCARate, years } = bundle;

  const parts: string[] = [];

  if (latestLocalRate != null && latestYear != null) {
    parts.push(
      `In ${latestYear}, ${location} had a violent crime rate of ${latestLocalRate.toFixed(0)} incidents per 100,000 residents`
    );
    if (latestCARate != null) {
      const diff = latestLocalRate - latestCARate;
      const dir = diff > 0 ? 'above' : 'below';
      parts[0] += `, ${Math.abs(diff).toFixed(0)} points ${dir} the California average of ${latestCARate.toFixed(0)} per 100k.`;
    } else {
      parts[0] += '.';
    }
  }

  if (latestLocalPercentile != null) {
    const publicSafetyScore = Math.round(100 - latestLocalPercentile);
    const safetyLabel =
      publicSafetyScore >= 75
        ? 'strong public safety (top quartile among CA places)'
        : publicSafetyScore >= 50
          ? 'above-median public safety'
          : publicSafetyScore >= 25
            ? 'below-median public safety'
            : 'lower public safety (bottom quartile among CA places)';
    parts.push(
      `This corresponds to a public safety score of ${publicSafetyScore} out of 100 — indicating ${safetyLabel}.`
    );
  }

  if (years.length >= 2) {
    const firstYear = years[0];
    const firstRate = bundle.localRatePer100k[0];
    if (firstRate != null && latestLocalRate != null) {
      const change = latestLocalRate - firstRate;
      const dir = change > 0 ? 'increased' : 'decreased';
      parts.push(
        `From ${firstYear} to ${latestYear}, the rate ${dir} by ${Math.abs(change).toFixed(0)} per 100k (${firstRate.toFixed(0)} → ${latestLocalRate.toFixed(0)}).`
      );
    }
  }

  return parts.join(' ') || `Public safety data loaded for ${location} (${latestYear}).`;
}
